import React, { useRef, useState, useEffect } from 'react';
import {Segmented, Card, Collapse, Row, Col, List, Typography} from 'antd';
import { ForceGraph2D, ForceGraph3D } from 'react-force-graph';
import { scaleSequential } from 'd3-scale';
import { interpolateViridis } from 'd3-scale-chromatic';
import useForceInteractions from "./useForceInteractions";
import {analyzeGraphData} from "./forceUtils";
import TickerChip from "../Components/TickerChip";
const { Panel } = Collapse;

const config = {
    defaultLinkColor: '#556677',
    nodeSize: 5,
    etfNodeSize: 50,
    initialZoom: 4 // Sets the initial zoom level
};


const Force2d3dToggle = ({ dimension, setDimension }) => {
    return (
        <Segmented options={['2D', '3D']} selected={dimension} onChange={setDimension} />
    )
}


const GraphStatsDisplay = ({ data, etfs }) => {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (data) {
            const computedStats = analyzeGraphData(data);
            setStats(computedStats);
        }
    }, [data]);

    if (!stats) {
        return <div>Loading stats...</div>;
    }

    console.log(stats, etfs);
    return (
        <Card bordered={false}>
            <Collapse defaultActiveKey={['1']} accordion>
                {Object.entries(stats.etfConnections).map(([etfId, details]) => (
                    <Card className={'gapless'} bordered={false} title={<TickerChip ticker={etfs?.[etfId]} />}>
                        <List>
                            {Object.entries(stats.etfOverlaps[etfId]).map(([otherEtfId, overlapCount]) =>
                                <List.Item>
                                    <List.Item.Meta title={<Typography.Text>Connections to {otherEtfId}</Typography.Text>} description={overlapCount} />
                                </List.Item>
                            )}
                        </List>
                        <Panel header={'Connected Stocks'} key={etfId} collapsible={true}>
                            {details.stocks.join(', ')}
                        </Panel>
                    </Card>
                ))}
            </Collapse>
        </Card>
    );
};


const ForceChart = ({ data, etfs, dimension = '2D' }) => {
    const fgRef = useRef();
    const [graphDimension, setGraphDimension] = useState(dimension);
    const { selectedNode, nodeColors, handleNodeClick } = useForceInteractions(data, fgRef);

    useEffect(() => {
        if (fgRef.current) {
            fgRef.current.zoom(config.initialZoom);
        }
    }, []);

    const nodeCanvasObject = (node, ctx, globalScale) => {
        const size = node.group > 0 ? config.etfNodeSize : config.nodeSize;
        const color = nodeColors[node.id] || '#667788';
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(node.x, node.y, size, 0, 2 * Math.PI, false);
        ctx.fill();

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#ffffff';
        const fontSize = node.group > 0 ? 16 : 10;
        ctx.font = `${fontSize}px Sans-Serif`;
        ctx.fillText(node.id, node.x, node.y);
    };

    const linkColor = link => config.defaultLinkColor;
    const ForceGraphComponent = graphDimension === '2D' ? ForceGraph2D : ForceGraph3D;

    return (
        <Row>
            <Col span={4}>
                <GraphStatsDisplay data={data} etfs={etfs} />
            </Col>
            <Col span={20}>
                <div className={'sunken-shadow p-3'}>
                    <Force2d3dToggle dimension={graphDimension} setDimension={setGraphDimension} />
                    <ForceGraphComponent
                        ref={fgRef}
                        graphData={data}
                        nodeCanvasObject={nodeCanvasObject}
                        linkColor={linkColor}
                        onNodeClick={handleNodeClick}
                    />
                </div>
            </Col>
        </Row>
    );
};

export default ForceChart;
